import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { iGenericResponse } from "@/customTypes/CommonServiceTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { liveSaleService } from "@/services/api/cresivoxServer";
import { iLiveSaleDTO, iLiveSaleResponse, iUpdateStatusPayload, REDUCER_NAME } from "./Types";

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

// Create Live Sale
// Update the addLiveSale thunk:
// In ThunkActions.ts
export const addLiveSale = createAsyncThunk<
  iGenericResponse<iLiveSaleResponse>,
  { data: iLiveSaleDTO },
  { rejectValue: iStateMessage }
>(curriedGetThunkName("addLiveSale"),
//@ts-ignore
 async ({ data }, { rejectWithValue }) => {
  try {
    const response = await liveSaleService.createLiveSale(data);
    
    if (!response) {
      return rejectWithValue({
        message: "No response received from server",
        httpStatusCode: 500
      });
    }

    if (response.httpStatusCode === httpStatusCodes.SUCCESS_CREATED) {
      return {
        data: response.data,
        message: "Live sale created successfully"
      };
    }
    
    return rejectWithValue({
      message: response.error || "Failed to create live sale",
      httpStatusCode: response.httpStatusCode
    });
    
  } catch (error: any) {
    console.error("Live sale creation error:", error);
    return rejectWithValue({
      message: error.message || "Something went wrong",
      httpStatusCode: error.httpStatusCode || 500
    });
  }
});

// Update Live Sale
export const updateLiveSale = createAsyncThunk<
  iGenericResponse<iLiveSaleResponse>,
  { liveSaleId: string; data: iLiveSaleDTO },
  { rejectValue: iStateMessage }
  //@ts-ignore
>(curriedGetThunkName("updateLiveSale"), async ({ liveSaleId, data }, { rejectWithValue }) => {
  try {
    const response = await liveSaleService.updateLiveSale(liveSaleId, data);
    if (response) {
      const { httpStatusCode, data: responseData, message } = response;

      if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
        // After update, fetch the complete sale data
        const updatedSale = await liveSaleService.getLiveSaleById(liveSaleId);
        return {
          data: updatedSale?.data,
          message: message || "Live sale updated successfully",
        };
      }
      return rejectWithValue({
        httpStatusCode,
        message: message || "Failed to update live sale",
      });
    }
    return rejectWithValue({
      message: "No response received from server",
    });
  } catch (error: any) {
    return rejectWithValue({
      message: error.message || "Something went wrong",
    });
  }
});

// Update Live Sale Status
// In ThunkAction.ts
export const updateLiveSaleStatus = createAsyncThunk<
  iGenericResponse<{ id: string; status: "Active" | "Expired" | "Upcoming" }>, // Update this line
  { liveSaleId: string; status: "Active" | "Expired" | "Upcoming" }, // Update this line
  { rejectValue: iStateMessage }
>(curriedGetThunkName("updateLiveSaleStatus"),
//@ts-ignore
 async ({ liveSaleId, status }, { rejectWithValue }) => {
  try {
    const response = await liveSaleService.updateLiveSaleStatus(liveSaleId, { status });
    if (response) {
      const { httpStatusCode, data: responseData, message } = response;

      if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
        return {
          data: { id: liveSaleId, status },
          message: message || "Live sale status updated successfully",
        };
      }
      return rejectWithValue({
        httpStatusCode,
        message: message || "Failed to update live sale status",
      });
    }
    return rejectWithValue({
      message: "No response received from server",
    });
  } catch (error: any) {
    return rejectWithValue({
      message: error.message || "Something went wrong",
    });
  }
});

// Delete Live Sale
export const deleteLiveSale = createAsyncThunk<
  iGenericResponse<{ id: string }>,
  { liveSaleId: string },
  { rejectValue: iStateMessage }
  //@ts-ignore
>(curriedGetThunkName("deleteLiveSale"), async ({ liveSaleId }, { rejectWithValue }) => {
  try {
    const response = await liveSaleService.deleteLiveSale(liveSaleId);
    if (response) {
      const { httpStatusCode, data: responseData, message } = response;

      if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
        return {
          data: { id: liveSaleId },
          message: message || "Live sale deleted successfully",
        };
      }
      return rejectWithValue({
        httpStatusCode,
        message: message || "Failed to delete live sale",
      });
    }
    return rejectWithValue({
      message: "No response received from server",
    });
  } catch (error: any) {
    return rejectWithValue({
      message: error.message || "Something went wrong",
    });
  }
});

// Get All Live Sales
export const getAllLiveSales = createAsyncThunk<
  iGenericResponse<iLiveSaleResponse[]>,
  void,
  { rejectValue: iStateMessage }
  //@ts-ignore
>(curriedGetThunkName("getAllLiveSales"), async (_, { rejectWithValue }) => {
  try {
    const response = await liveSaleService.getAllLiveSales();
    if (response) {
      const { httpStatusCode, data: responseData, message } = response;

      if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
        return {
          data: responseData,
          message: message || "Live sales fetched successfully",
        };
      }
      return rejectWithValue({
        httpStatusCode,
        message: message || "Failed to fetch live sales",
      });
    }
    return rejectWithValue({
      message: "No response received from server",
    });
  } catch (error: any) {
    return rejectWithValue({
      message: error.message || "Something went wrong",
    });
  }
});

// Get Active Live Sales
export const getActiveLiveSales = createAsyncThunk<
  iGenericResponse<iLiveSaleResponse[]>,
  void,
  { rejectValue: iStateMessage }
  //@ts-ignore
>(curriedGetThunkName("getActiveLiveSales"), async (_, { rejectWithValue }) => {
  try {
    const response = await liveSaleService.getActiveLiveSales();
    if (response) {
      const { httpStatusCode, data: responseData, message } = response;

      if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
        return {
          data: responseData,
          message: message || "Active live sales fetched successfully",
        };
      }
      return rejectWithValue({
        httpStatusCode,
        message: message || "Failed to fetch active live sales",
      });
    }
    return rejectWithValue({
      message: "No response received from server",
    });
  } catch (error: any) {
    return rejectWithValue({
      message: error.message || "Something went wrong",
    });
  }
});

// Get Live Sale By ID
export const getLiveSaleById = createAsyncThunk<
  iGenericResponse<iLiveSaleResponse>,
  { liveSaleId: string },
  { rejectValue: iStateMessage }
  //@ts-ignore
>(curriedGetThunkName("getLiveSaleById"), async ({ liveSaleId }, { rejectWithValue }) => {
  try {
    const response = await liveSaleService.getLiveSaleById(liveSaleId);
    if (response) {
      const { httpStatusCode, data: responseData, message } = response;

      if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
        return {
          data: responseData,
          message: message || "Live sale fetched successfully",
        };
      }
      return rejectWithValue({
        httpStatusCode,
        message: message || "Failed to fetch live sale",
      });
    }
    return rejectWithValue({
      message: "No response received from server",
    });
  } catch (error: any) {
    return rejectWithValue({
      message: error.message || "Something went wrong",
    });
  }
});